import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import {
    type TriggerTestBatchParams,
    type TriggerTestBatchResponse,
    type BatchStatusResponse,
    type CancelTestBatchResponse,
    type SaveLearnerProjectParams,
    type SaveLearnerProjectResponse,
    type SubmissionComparisonData,
    type ProjectDocumentInfo,
    type ApiProjectDocumentInfo,
    type PromptVersionsResponse,
} from '../AutogradingTestWizard.types';

type ApiSubmissionComparisonData =
    | { wasAutograded: false; finalLastGradedAt: number | null }
    | {
          wasAutograded: true;
          provisionalScore: number | null;
          provisionalFeedback: string | null;
          provisionalGradedAt: number | null;
          finalLastGradedAt: number | null;
          originalPrompt: string | null;
          originalProjectDocuments: ApiProjectDocumentInfo[] | null;
          originalPrivateProjectDocuments: ApiProjectDocumentInfo[] | null;
      };

export function normalizeDoc(doc: ApiProjectDocumentInfo): ProjectDocumentInfo {
    return { id: doc.id, file_file_name: doc.fileFileName, url: doc.url, title: doc.title };
}

export const autogradingTestWizardApi = createApi('AutogradingTestWizardApi', {
    ...getBackRoyalApiBaseConfig('AutogradingTestWizardApi'),
    // oxlint-disable-next-line max-lines-per-function
    endpoints: builder => ({
        triggerTestBatch: builder.mutation<TriggerTestBatchResponse, TriggerTestBatchParams>({
            query: ({ learnerProjectId, submissionIds }) => ({
                url: 'autograding_sessions/trigger_test_batch.json',
                method: 'POST',
                body: { learnerProjectId, submissionIds },
            }),
        }),

        getBatchStatus: builder.query<BatchStatusResponse, { batchId: string; submissionIds: string[] }>({
            query: ({ batchId, submissionIds }) => ({
                url: 'autograding_sessions/batch_status.json',
                method: 'GET',
                params: { batchId, 'submissionIds[]': submissionIds },
            }),
        }),

        cancelTestBatch: builder.mutation<CancelTestBatchResponse, string>({
            query: batchId => ({
                url: 'autograding_sessions/cancel_test_batch.json',
                method: 'POST',
                body: { batchId },
            }),
        }),

        saveLearnerProject: builder.mutation<SaveLearnerProjectResponse, SaveLearnerProjectParams>({
            query: ({ id, record }) => ({
                url: `learner_projects/${id}.json`,
                method: 'PUT',
                body: { record },
            }),
            // The standard CRUD controller wraps the response in { contents: { learnerProjects: [...] } }.
            // Extract the record so callers get a flat SaveLearnerProjectResponse.
            transformResponse: (response: { contents?: { learnerProjects?: SaveLearnerProjectResponse[] } }) => {
                const record = response?.contents?.learnerProjects?.[0];
                if (!record) throw new Error('Unexpected response shape from learner project save');
                return record;
            },
        }),

        getSubmissionComparisonData: builder.query<SubmissionComparisonData, { projectSubmissionId: string }>({
            query: ({ projectSubmissionId }) => ({
                url: 'autograding_sessions/submission_comparison_data.json',
                method: 'GET',
                params: { projectSubmissionId },
            }),
            transformResponse: (response: ApiSubmissionComparisonData): SubmissionComparisonData => {
                if (!response.wasAutograded) return response;
                return {
                    ...response,
                    originalProjectDocuments: response.originalProjectDocuments?.map(normalizeDoc) ?? null,
                    originalPrivateProjectDocuments:
                        response.originalPrivateProjectDocuments?.map(normalizeDoc) ?? null,
                };
            },
        }),

        getPromptVersions: builder.query<PromptVersionsResponse, { learnerProjectId: string; variant?: string }>({
            query: ({ learnerProjectId, variant }) => ({
                url: `learner_projects/${learnerProjectId}/prompt_versions.json`,
                method: 'GET',
                params: variant ? { variant } : {},
            }),
        }),
    }),
});

export const {
    useTriggerTestBatchMutation,
    useLazyGetBatchStatusQuery,
    useCancelTestBatchMutation,
    useSaveLearnerProjectMutation,
    useLazyGetSubmissionComparisonDataQuery,
    useGetPromptVersionsQuery,
} = autogradingTestWizardApi;
